import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LoopIcon from '@mui/icons-material/Loop';
import {
  Alert, Button, Card, CardActions, CardHeader,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AvailabilityCardContent from './AvailabilityCardContent';
import { pushSnackbar } from 'FRONTEND/actions/app';
import { CANCEL_REASON, CONFIRM_REASON, DELETE_REASON } from 'FRONTEND/components/Calendar/CalendarPopover/closeReasons';
import { isScheduleAvailable } from 'FRONTEND/components/Calendar/utils';
import InitialsAvatar from 'FRONTEND/components/InitialsAvatar';
import {
  createRequest as createAvailabilityRequest,
  createRecurrenceRequest,
  deleteRequest as deleteAvailabilityRequest,
  edit as editAvailability,
  selectExpertAvailabilities,
  startEditing as startEditingAvailability,
  stopEditing as stopEditingAvailability,
  updateRequest as updateAvailabilityRequest,
} from 'FRONTEND/reducers/availabilitySlice';
import { selectHasRight, selectUserTenantIds } from 'FRONTEND/reducers/userSlice';
import { selectCalendarActor } from 'FRONTEND/selectors/calendarSelectors';
import {
  DRAFT_AVAILABILITY_ID,
  RESOURCE,
} from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { RIGHTS } from 'FRONTEND/utils/rights';

const AvailabilityPreview = ({
  availability,
  onClose,
}) => {
  const dispatch = useDispatch();
  // Locale management
  const locale = useSelector((state) => state.user.locale);
  dayjs.locale(locale);

  // All tenants associated with current user
  const allCurrentUserTenants = useSelector(selectUserTenantIds);
  // Check if current user is missing tenants on existing availability.
  const isMissingTenant = useMemo(() => {
    return availability?.tenantList?.some(
      (availabilityTenant) => !allCurrentUserTenants.includes(availabilityTenant),
    );
  }, [availability?.tenantList, allCurrentUserTenants]);

  // rights
  const hasAvailabilityCreateRight = useSelector((state) => selectHasRight(state, RIGHTS.AVAILABILITIES_CREATE));
  const hasAvailabilityUpdateRight = useSelector((state) => selectHasRight(state, RIGHTS.AVAILABILITIES_UPDATE));
  const hasAvailabilityDeleteRight = useSelector((state) => selectHasRight(state, RIGHTS.AVAILABILITIES_DELETE));

  // Init an availability draft with all relevant tenants auto-selected.
  // Relevant tenants are either the current user tenants or the managed expert tenants.
  // When there is only 1 tenant in the array, we hide the TenantSelector in the JSX
  const currentUserId = useSelector((state) => state.user.id);
  const managedExpert = useSelector((state) => state.expert.entities[availability.actorId]);
  useEffect(() => {
    if (managedExpert && availability && !isResourceActor && availability.availabilityId === DRAFT_AVAILABILITY_ID) {
      dispatch(editAvailability({
        ...availability,
        tenantList: availability.actorId === currentUserId ? allCurrentUserTenants : managedExpert.tenantList,
      }));
    }
  }, []);

  // Deletion management
  const handleDeleteAvailabilityClick = () => {
    dispatch(deleteAvailabilityRequest({
      availabilityId: availability.availabilityId,
      startDate: dayjs(availability.startDate).startOf('week').toString(),
      endDate: dayjs(availability.endDate).endOf('week').toString(),
    }));
    onClose(DELETE_REASON);
  };

  // Recurrence management
  const [isRecurrenceDialogOpen, setIsRecurrenceDialogOpen] = useState(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(dayjs().add(7, 'days'));
  const openRecurrenceDialog = () => {
    setRecurrenceEndDate(dayjs(availability.startDate).add(7, 'days'));
    setIsRecurrenceDialogOpen(true);
  };
  const closeRecurrenceDialog = () => {
    setIsRecurrenceDialogOpen(false);
  };
  const submitRecurrenceDate = () => {
    dispatch(createRecurrenceRequest({
      availabilityId: availability.availabilityId,
      endDate: recurrenceEndDate.toISOString(),
      actorId: availability.actorId,
    }));
    closeRecurrenceDialog();
    onClose(CONFIRM_REASON);
  };

  // Draft and edition management
  const isEditing = availability.isEditing || availability.availabilityId === DRAFT_AVAILABILITY_ID;
  const isResourceActor = availability.actorType === RESOURCE;
  const handleConfirmClick = () => {
    if (availability.availabilityId === DRAFT_AVAILABILITY_ID) {
      dispatch(createAvailabilityRequest({
        startDate: availability.startDate,
        endDate: availability.endDate,
        typeList: availability.typeList,
        actorId: availability.actorId,
        actorType: availability.actorType,
        locationId: availability.location?.id,
        tenantList: availability.tenantList,
      }));
    } else {
      dispatch(updateAvailabilityRequest({
        availabilityId: availability.availabilityId,
        actorId: availability.actorId,
        actorType: availability.actorType,
        startDate: availability.startDate,
        endDate: availability.endDate,
        typeList: availability.typeList,
        locationId: availability.location?.id,
        tenantList: availability.tenantList,
      }));
      dispatch(stopEditingAvailability({ id: availability.availabilityId, shouldRestore: false }));
    }
    onClose(CONFIRM_REASON);
  };
  const handleCancelClick = () => {
    if (isEditing) {
      dispatch(stopEditingAvailability({ id: availability.availabilityId, shouldRestore: true }));
    }
    onClose(CANCEL_REASON);
  };
  const handleEditClick = () => {
    dispatch(startEditingAvailability({ id: availability.availabilityId }));
  };

  // Start & end management
  const availabilityList = useSelector((state) => selectExpertAvailabilities(state, availability.actorId).filter(({ availabilityId }) => availabilityId !== availability.availabilityId));
  const editIfAvailable = (editedAvailability) => {
    // Managing invalid start and end when editing TimePicker with keyboard.
    if (!editedAvailability.startDate || !editedAvailability.endDate) {
      return;
    }
    // Prevent CalendarPopover disappearance when changing end to 0:00 in TimePicker with keyboard.
    const endDate = dayjs(editedAvailability.endDate);
    if (endDate.get('hour') === 0 && endDate.get('minute') === 0) {
      editedAvailability.endDate = endDate.set('hour', 23).set('minute', 55).toDate().toJSON();
    }
    if (isScheduleAvailable({
      availabilities: availabilityList,
      startDate: editedAvailability.startDate,
      endDate: editedAvailability.endDate,
    })) {
      dispatch(editAvailability({ ...editedAvailability }));
    } else {
      dispatch(pushSnackbar({
        message: i18n._('Availability cannot be changed or moved there because it would overlap with another one.'),
        variant: 'warning',
      }));
    }
  };
  const handleDateChange = (date) => {
    // Date(null) will get Thu Jan 01 1970 00:00:00, so we have to return here.
    if (!date) {
      return;
    }
    const newDate = new Date(date);
    const endDate = new Date(availability.endDate);
    endDate.setFullYear(newDate.getFullYear());
    endDate.setMonth(newDate.getMonth());
    endDate.setDate(newDate.getDate());
    editIfAvailable({ ...availability, startDate: newDate.toJSON(), endDate: endDate.toJSON() });
  };
  const handleStartChange = (startDate) => {
    if (startDate?.isValid()) {
      editIfAvailable({ ...availability, startDate: startDate.toJSON() });
    }
  };
  const handleEndChange = (endDate) => {
    if (endDate?.isValid()) {
      editIfAvailable({ ...availability, endDate: endDate.toJSON() });
    }
  };

  // Loading management
  const isLoading = useSelector((state) => state.app.requestCounter[updateAvailabilityRequest.toString()] > 0);

  // Layout management
  const calendarActor = useSelector(selectCalendarActor);

  const handleCloseClick = () => {
    onClose(CANCEL_REASON);
  };

  const handleLocationSelect = (locationId) => {
    dispatch(editAvailability({ ...availability, location: { id: locationId } }));
  };

  return (
    <Card>
      <CardHeader
        avatar={
          <InitialsAvatar
            picture={calendarActor?.picture}
            initials={getInitials(calendarActor?.alias)}
            sideSize={40}
          />
        }
        action={
          <IconButton aria-label='close' onClick={handleCloseClick} size='large'>
            <CloseIcon />
          </IconButton>
        }
        subheader={`${dayjs(availability.startDate).format('LLLL')} - ${dayjs(availability.endDate).format('LT')}`}
        title={
          <>
            <Typography variant='body2' sx={{ fontWeight: 'bold' }} component='span'>{i18n._('Availability')}</Typography> - <Typography component='span' variant='body2' color='textSecondary'>{calendarActor?.alias}</Typography>
          </>
        }
        className='availabilityPreview'
      />
      <AvailabilityCardContent
        availability={availability}
        isEditing={isEditing}
        isLoading={isLoading}
        isResourceActor={isResourceActor}
        onDateChange={handleDateChange}
        onStartChange={handleStartChange}
        onEndChange={handleEndChange}
        onLocationSelect={handleLocationSelect}
      />
      <CardActions>
        {isMissingTenant && (
          <Alert
            severity='error'
            sx={{
              maxWidth: '350px',
            }}
          >
            {i18n._('This availability has been shared on other environments to which you do not have access. Please contact Parsys support.')}
          </Alert>
        )}
        {!isMissingTenant && (
          isEditing ? (
            <>
              <Button
                disabled={availability.typeList.length === 0 || (!isResourceActor && availability.tenantList.length === 0) || (isResourceActor && !availability.location?.id)}
                variant='contained'
                onClick={handleConfirmClick}
              >{i18n._('Confirm')}
              </Button>
              <Button variant='outlined' onClick={handleCancelClick}>{i18n._('Cancel')}</Button>
            </>
          ) : (
            <>
              {hasAvailabilityCreateRight && <Button variant='outlined' onClick={openRecurrenceDialog} startIcon={<LoopIcon />}>{i18n._('Recurrence')}</Button>}
              {hasAvailabilityUpdateRight && <Button variant='outlined' onClick={handleEditClick} startIcon={<EditIcon />}>{i18n._('Edit')}</Button>}
              {hasAvailabilityDeleteRight && <Button variant='outlined' onClick={handleDeleteAvailabilityClick} startIcon={<DeleteIcon />}>{i18n._('Delete')}</Button>}
            </>
          ))}
      </CardActions>
      {/* Preventing a focus loop issue between the Dialog and parent PopOver with disableEnforceFocus. The keyboard cannot be used because of that, but we don't need it anyway. */}
      <Dialog open={isRecurrenceDialogOpen} onClose={closeRecurrenceDialog} aria-labelledby='recurrence-dialogTitle' sx={{ zIndex: 3000 }} disableEnforceFocus>
        <DialogTitle id='recurrence-dialogTitle'>{i18n._('Weekly recurrence')}</DialogTitle>
        <DialogContent>
          <Typography>{i18n._('End of recurrence')}:</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
            <DatePicker
              value={recurrenceEndDate}
              onChange={setRecurrenceEndDate}
              format='ll'
              minDate={dayjs(availability.startDate).add(7, 'days')}
              slotProps={{
                popper: { sx: { zIndex: 3500 } },
                textField: { size: 'small' },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecurrenceDialog} variant='outlined'>{i18n._('Cancel')}</Button>
          <Button onClick={submitRecurrenceDate} variant='contained'>{i18n._('Submit')}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AvailabilityPreview;
